import { useGetFeatureUsages } from '@modusign/openapi-client-subscription';
import { CamelCase } from 'ts-essentials';

import { MAX_ATTACHMENT_REQUEST_COUNT, MAX_SIGNER_COUNT } from '@app/constants/documentSetup';
import { useSubscription } from '@app/features/subscription/hooks/subscription';
import { FeatureType } from '@app/features/subscription/types/FeatureType';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import LimitType from '@app/store/user/authority/LimitType';

type PartialFeatureType<T extends FeatureType> = [T] extends [FeatureType] ? T : never;

/** quota : 사용량 확인 시, 현재 개수와 상관 없이 남아있는 사용량과 비교되는 값 */
export type Quota = PartialFeatureType<
  | 'SIGNING_REQUEST_COUNT'
  | 'BULK_REQUEST_COUNT'
  | 'CONTRACT_LINK_COMPLETED_DOCUMENT_COUNT'
  | 'API_SIGNING_REQUEST_COUNT'
>;
export type QuotaUsages = {
  [K in CamelCase<Quota>]: {
    limit: number;
    usage: number;
  };
};
const initialQuotaUsages: QuotaUsages = {
  signingRequestCount: {
    limit: Infinity,
    usage: 0,
  },
  bulkRequestCount: {
    limit: Infinity,
    usage: 0,
  },
  contractLinkCompletedDocumentCount: {
    limit: Infinity,
    usage: 0,
  },
  apiSigningRequestCount: {
    limit: Infinity,
    usage: 0,
  },
};

/** throttle : 사용량 확인 시, 허용된 최대 사용량과 비교되는 값 (throttle의 usage는 별도의 현재 사용량 확인이 필요합니다.) */
type Throttle = PartialFeatureType<
  | 'EXTERNAL_DOCUMENT_COUNT'
  | 'WORKSPACE_SEAT_COUNT'
  | 'ATTACHMENT_REQUEST_COUNT'
  | 'TEMPLATE_COUNT'
  | 'SIGNER_COUNT'
>;
type ThrottleUsages = {
  [K in CamelCase<Throttle>]: {
    limit: number;
  };
};
const initialThrottleUsages: ThrottleUsages = {
  externalDocumentCount: {
    limit: Infinity,
  },
  workspaceSeatCount: {
    limit: Infinity,
  },
  attachmentRequestCount: {
    limit: MAX_ATTACHMENT_REQUEST_COUNT,
  },
  templateCount: {
    limit: Infinity,
  },
  signerCount: {
    limit: MAX_SIGNER_COUNT,
  },
};

type UsageCount = QuotaUsages & ThrottleUsages;
const initialUsageCount: UsageCount = {
  ...initialQuotaUsages,
  ...initialThrottleUsages,
};

/** TODO(@highcastlee): planVersion 0인 요금제가 모두 종료되면, planVersion 분기는 제거되어야합니다. (23.12.01 ~) */
export default function useUsageCount(): UsageCount & { isLoading: boolean } {
  const workspaceId = useWorkspaceId();

  const { data: subscription, isLoading: isSubscriptionLoading } = useSubscription();
  const { data: featureUsages, isLoading: isFeatureUsagesLoading } = useGetFeatureUsages(
    workspaceId,
    {
      query: {
        select: (data) => {
          const limits: Limits = { ...initialLimits };
          const usages: Usages = { ...initialUsages };

          if (data) {
            data.limits.forEach((l) => {
              limits[l.feature] = l;
            });
            data.usages.forEach((u) => {
              usages[u.feature] = u.count;
            });
          }

          return { period: data?.period || null, limits, usages };
        },
        staleTime: 1000 * 60 * 5,
      },
    }
  );

  const isLoading = isFeatureUsagesLoading || isSubscriptionLoading;

  if (!featureUsages || !subscription?.plan) {
    return { isLoading, ...initialUsageCount };
  }

  let countsByPlanVersion = {} as Pick<
    UsageCount,
    | 'signingRequestCount'
    | 'bulkRequestCount'
    | 'contractLinkCompletedDocumentCount'
    | 'signerCount'
  >;

  if (!subscription.plan.planVersion) {
    // planVersion이 0이면 기존 요금제. 즉, 서명요청, 대량전송, 링크서명 각각의 개수 반환
    countsByPlanVersion = {
      signingRequestCount: {
        limit: featureUsages.limits.SIGNING_REQUEST_COUNT?.count ?? Infinity,
        usage: featureUsages.usages.SIGNING_REQUEST_COUNT,
      },
      bulkRequestCount: {
        limit: featureUsages.limits.BULK_REQUEST_COUNT?.count ?? Infinity,
        usage: featureUsages.usages.BULK_REQUEST_COUNT,
      },
      contractLinkCompletedDocumentCount: {
        limit: featureUsages.limits.CONTRACT_LINK_COMPLETED_DOCUMENT_COUNT?.count ?? Infinity,
        usage: featureUsages.usages.CONTRACT_LINK_COMPLETED_DOCUMENT_COUNT,
      },
      signerCount: {
        limit: MAX_SIGNER_COUNT,
      },
    };
  } else if (subscription.plan.planVersion === 1) {
    // planVersion이 1이면 신규 요금제. 즉, document_count 통합 사용량(단건, 대량, 링크) 반환
    countsByPlanVersion = {
      signingRequestCount: {
        limit: featureUsages.limits.DOCUMENT_COUNT?.count ?? Infinity,
        usage: featureUsages.usages.DOCUMENT_COUNT,
      },
      bulkRequestCount: {
        limit: featureUsages.limits.DOCUMENT_COUNT?.count ?? Infinity,
        usage: featureUsages.usages.DOCUMENT_COUNT,
      },
      contractLinkCompletedDocumentCount: {
        limit: featureUsages.limits.DOCUMENT_COUNT?.count ?? Infinity,
        usage: featureUsages.usages.DOCUMENT_COUNT,
      },
      signerCount: {
        limit: featureUsages.limits.SIGNER_COUNT?.count ?? MAX_SIGNER_COUNT,
      },
    };
  }

  return {
    isLoading,
    ...countsByPlanVersion,
    apiSigningRequestCount: {
      limit: featureUsages.limits.API_SIGNING_REQUEST_COUNT?.count ?? Infinity,
      usage: featureUsages.usages.API_SIGNING_REQUEST_COUNT,
    },
    externalDocumentCount: {
      limit: featureUsages.limits.EXTERNAL_DOCUMENT_COUNT?.count ?? Infinity,
    },
    workspaceSeatCount: {
      limit: featureUsages.limits.WORKSPACE_SEAT_COUNT?.count ?? Infinity,
    },
    attachmentRequestCount: {
      limit: featureUsages.limits.ATTACHMENT_REQUEST_COUNT?.count ?? MAX_ATTACHMENT_REQUEST_COUNT,
    },
    templateCount: {
      limit: featureUsages.limits.TEMPLATE_COUNT?.count ?? Infinity,
    },
  };
}

type Limit = {
  count: number;
  feature: FeatureType;
  id: string;
  type: LimitType;
};
type Limits = Record<FeatureType, Limit | null>;
type Usages = Record<FeatureType, number>;

const initialLimits: Limits = {
  [FeatureType.API_SIGNING_REQUEST_COUNT]: null,
  [FeatureType.ATTACHMENT_REQUEST_COUNT]: null,
  [FeatureType.BULK_REQUEST_COUNT]: null,
  [FeatureType.CONTRACT_LINK_COMPLETED_DOCUMENT_COUNT]: null,
  [FeatureType.DOCUMENT_COUNT]: null,
  [FeatureType.EXTERNAL_DOCUMENT_COUNT]: null,
  [FeatureType.KAKAO_NOTIFICATION_COUNT]: null,
  [FeatureType.MOBILE_VERIFICATION_COUNT]: null,
  [FeatureType.WORKSPACE_SEAT_COUNT]: null,
  [FeatureType.SIGNING_REQUEST_COUNT]: null,
  [FeatureType.TEMPLATE_COUNT]: null,
  [FeatureType.SIGNER_COUNT]: null,
};
const initialUsages: Usages = {
  [FeatureType.API_SIGNING_REQUEST_COUNT]: 0,
  [FeatureType.ATTACHMENT_REQUEST_COUNT]: 0,
  [FeatureType.BULK_REQUEST_COUNT]: 0,
  [FeatureType.CONTRACT_LINK_COMPLETED_DOCUMENT_COUNT]: 0,
  [FeatureType.DOCUMENT_COUNT]: 0,
  [FeatureType.EXTERNAL_DOCUMENT_COUNT]: 0,
  [FeatureType.KAKAO_NOTIFICATION_COUNT]: 0,
  [FeatureType.MOBILE_VERIFICATION_COUNT]: 0,
  [FeatureType.WORKSPACE_SEAT_COUNT]: 0,
  [FeatureType.SIGNING_REQUEST_COUNT]: 0,
  [FeatureType.TEMPLATE_COUNT]: 0,
  [FeatureType.SIGNER_COUNT]: 0,
};
